import {
  Anchor,
  Badge,
  Group,
  LoadingOverlay,
  Pagination,
  ScrollArea,
  Select,
  Table,
  Text,
} from "@mantine/core";
import { DateFilter } from "components/common/filters";
import { DateField, EditButton, List, ShowButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef, Header } from "@tanstack/react-table";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { TeaAddonSubscription } from "entities/tea_addon_subscription";
import {
  colorsByStatus,
  selectStatuses,
} from "entities/tea_addon_subscription/status";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { formatToJst } from "utils/dates";


export const TeaAddonSubscriptionList: React.FC = () => {
  const columns = useMemo<ColumnDef<TeaAddonSubscription>[]>(
    () => [
      {
        id: "id",
        header: "ID",
        accessorKey: "id",
      },
      {
        id: "user",
        header: "User",
        accessorKey: "user",
        cell: function render({ getValue }) {
          const user = getValue<TeaAddonSubscription["user"]>();

          return (
            <Anchor component={Link} to={`/users/show/${user?.id}`}>
              {user?.email}
            </Anchor>
          );
        },
        meta: {
          disableSort: true,
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: function render({ getValue }) {
          const status = getValue<TeaAddonSubscription["status"]>();

          return <Badge color={colorsByStatus[status]}>{status}</Badge>;
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<TeaAddonSubscription, any>;
          }) => {
            return (
              <div>
                <Select
                  placeholder="Status"
                  data={selectStatuses}
                  onChange={header.column.setFilterValue}
                  clearable
                />
              </div>
            );
          },
        },
      },
      {
        id: "subscription",
        header: "Subscription",
        accessorKey: "subscription",
        cell: function render({ getValue }) {
          const subscription = getValue<TeaAddonSubscription["subscription"]>();
          if (!subscription) return <div />;

          return (
            <Anchor
              component={Link}
              to={`/subscriptions/show/${subscription.id}`}
            >
              {subscription.remote_id}
            </Anchor>
          );
        },
        meta: {
          disableSort: true,
        },
      },
      {
        id: "started_at",
        header: "Started At",
        accessorKey: "started_at",
        cell: function render({ getValue }) {
          const startedAt = getValue<TeaAddonSubscription["started_at"]>();

          return <DateField value={startedAt} format="DD/MM/YYYY HH:mm:ss" />;
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<TeaAddonSubscription, any>;
          }) => <DateFilter header={header} />,
        },
      },
      {
        id: "last_payment_at",
        header: "Last Payment At",
        accessorKey: "last_payment_at",
        cell: function render({ getValue }) {
          const lastPaymentAt =
            getValue<TeaAddonSubscription["last_payment_at"]>();

          return (
            <DateField value={lastPaymentAt} format="DD/MM/YYYY HH:mm:ss" />
          );
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<TeaAddonSubscription, any>;
          }) => <DateFilter header={header} />,
        },
      },
      {
        id: "next_renewal_at",
        header: "Next Renewal At",
        accessorKey: "next_renewal_at",
        cell: function render({ getValue }) {
          const nextRenewalAt =
            getValue<TeaAddonSubscription["next_renewal_at"]>();

          if (!nextRenewalAt) return <Text>-</Text>;

          return (
            <DateField value={nextRenewalAt} format="DD/MM/YYYY HH:mm:ss" />
          );
        },
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId) as Date | null | undefined;
          const b = rowB.getValue(columnId) as Date | null | undefined;
          
          if (!a && !b) return 0;
          if (!a) return 1; // null values go to the end
          if (!b) return -1; // null values go to the end
          
          return new Date(a).getTime() - new Date(b).getTime();
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<TeaAddonSubscription, any>;
          }) => <DateFilter header={header} />,
        },
      },
      {
        id: "canceled_at",
        header: "Canceled At",
        accessorKey: "canceled_at",
        cell: function render({ getValue }) {
          const canceledAt = getValue<TeaAddonSubscription["canceled_at"]>();

          if (!canceledAt) return <Text>-</Text>

          return <DateField value={canceledAt} format="DD/MM/YYYY HH:mm:ss" />;
        },
        sortingFn: (rowA, rowB, columnId) => {
          const a = rowA.getValue(columnId) as Date | null | undefined;
          const b = rowB.getValue(columnId) as Date | null | undefined;
          
          if (!a && !b) return 0;
          if (!a) return 1; // null values go to the end
          if (!b) return -1; // null values go to the end
          
          return new Date(a).getTime() - new Date(b).getTime();
        },
        meta: {
          filterElement: ({
            header,
          }: {
            header: Header<TeaAddonSubscription, any>;
          }) => <DateFilter header={header} />,
        },
      },
      {
        id: "actions",
        header: "Actions",
        accessorKey: "id",
        cell: function render({ getValue }) {
          return (
            <Group spacing="xs" noWrap>
              <ShowButton hideText recordItemId={getValue() as number} />
              <EditButton hideText recordItemId={getValue() as number} />
            </Group>
          );
        },
      },
    ],
    [],
  );

  const {
    getHeaderGroups,
    getRowModel,
    setOptions,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      pageCount,
      current,
      tableQueryResult: { data: tableData, isFetching },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      sorters: {
        initial: [
          {
            field: "id",
            order: "desc",
          },
        ],
      },
      pagination: {
        pageSize: 50,
      },
    },
  });

  return (
    <List>
      <AppliedFilters<TeaAddonSubscription> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<TeaAddonSubscription> headerGroups={getHeaderGroups()} />
          <Rows<TeaAddonSubscription> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <br />
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
